import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/services", label: "Services", id: "services" },
  { to: "/about", label: "About", id: "about" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="nav-header" className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-500 ${scrolled || open ? "bg-[#0A192F]/90 backdrop-blur-xl shadow-[0_1px_0_rgba(255,255,255,0.08)]" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo"><Logo dark /></Link>
        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end data-testid={`nav-link-${l.id}`}
              className={({ isActive }) => `relative px-4 py-2 text-sm font-medium transition-colors ${isActive ? "text-white" : "text-slate-300 hover:text-white"}`}>
              {({ isActive }) => (<>{l.label}{isActive && <motion.span layoutId="nav-dot" className="absolute left-1/2 -bottom-0.5 w-1 h-1 rounded-full bg-sky-400" />}</>)}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/contact#enquiry" data-testid="btn-request-care-nav"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-white text-[#0A192F] pl-5 pr-2 py-2 text-sm font-semibold group hover:bg-sky-300 transition-colors">
            Make an enquiry
            <span className="w-7 h-7 rounded-full bg-[#0A192F] text-white grid place-items-center transition-transform duration-300 group-hover:rotate-45"><ArrowUpRight size={14} /></span>
          </Link>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="md:hidden text-white p-2" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="md:hidden overflow-hidden" data-testid="nav-mobile-menu">
            <div className="px-5 pb-8 pt-2 flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <NavLink to={l.to} end data-testid={`nav-mobile-link-${l.id}`} className="block py-3 text-3xl font-bold tracking-tight text-white border-b border-white/10">{l.label}</NavLink>
                </motion.div>
              ))}
              <Link to="/contact#enquiry" data-testid="nav-mobile-enquiry" className="mt-6 rounded-full bg-sky-400 text-[#0A192F] text-center py-4 font-semibold">Make an enquiry</Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
